import { Result, Schema } from "effect";
import { canonicalJsonStringify } from "../../canonical-json/serialize.ts";
import { canonicalJsonCause } from "../../canonical-json/cause.ts";
import { IdentityArrayInput, UniqueIdentityValues } from "../../identity-input.ts";
import { ContractDecodeError } from "../../errors/validation.ts";
import { FindingDispositionError } from "../../errors/review.ts";
import { FindingCategory, FindingSeverity } from "../change-review/model.ts";
import { StationRole } from "../../development-authorization.ts";
import { developmentIdentifier } from "../../development-identifiers.ts";
import type { DurableFindingDispositionRecord, DurableFindingRecord } from "../../development-coordinator-model.ts";
import type { FindingDispositionRow } from "../../development-coordinator-storage-model.ts";
import { FINDING_DISPOSITION_VERSION, FindingDispositionAction, FindingLifecycleState, FindingGateEffect, FindingActorScope,
  PositiveInteger, DispositionTime, StableToken, DispositionReason, FindingDispositionArtifact, FindingDispositionIdentity,
  FindingDispositionSigner, findingDispositionDependencyError, type FindingDispositionDependencyError,
  type FindingDispositionSnapshotInput, type FindingAuthorityGrantInput, type FindingDispositionInput } from "./model.ts";

type Refusal = (code: FindingDispositionError["code"], message: string) => never;
export type FindingDispositionFailure = FindingDispositionError | FindingDispositionDependencyError;
const dispositionResult = <A>(body: (refuse: Refusal) => A): Result.Result<A, FindingDispositionFailure> => {
  let owned: FindingDispositionError | undefined;
  return Result.try({
    try: () => body((code, message) => Result.getOrThrowWith(Result.fail(owned = new FindingDispositionError(code, message)), value => value)),
    catch: cause => owned !== undefined && cause === owned ? owned : findingDispositionDependencyError(cause)
  });
};
const parse = <S extends Schema.ConstraintDecoder<unknown>>(schema: S,
  onError: (error: Schema.SchemaError) => unknown = error => new ContractDecodeError({ message: error.message })) => {
  const decode = Schema.decodeUnknownResult(schema);
  return (value: unknown): S["Type"] => Result.getOrThrowWith(decode(value), onError);
};
const typeBoundary = <S extends Schema.ConstraintDecoder<unknown>>(schema: S, message?: string) =>
  parse(schema, () => new TypeError(message));
const serialize = (value: unknown) => Result.getOrThrowWith(canonicalJsonStringify(value), canonicalJsonCause);

const ROLE_RANK = Object.freeze({ reader: 0, runner: 1, reviewer: 2, maintainer: 3, owner: 4 });
const dispositionAction = /* @__PURE__ */ (() => typeBoundary(FindingDispositionAction, "Finding disposition action is invalid."))();

function snapshot<Value>(value: Value): Value {
  return JSON.parse(serialize(value)) as Value;
}

const role = (value: unknown, label: string): StationRole =>
  typeBoundary(StationRole, `${label} is invalid.`)(value);

function atLeastRole(observed: StationRole, required: StationRole): boolean {
  return ROLE_RANK[observed] >= ROLE_RANK[required];
}

const positiveInteger = (value: unknown, label: string): number =>
  parse(PositiveInteger, () => new RangeError(`${label} must be a positive safe integer.`))(value);
const safeTime = /* @__PURE__ */ (() => parse(DispositionTime, () => new RangeError("Finding disposition time must be non-negative.")))();
const token = (value: unknown, label: string): string =>
  typeBoundary(StableToken, `${label} must be a stable token.`)(value);
const boundedText = /* @__PURE__ */ (() => typeBoundary(DispositionReason, "Finding disposition reason must be bounded text."))();
const findingLifecycle = /* @__PURE__ */ (() => typeBoundary(FindingLifecycleState, "Finding lifecycle is invalid."))();
const severity = /* @__PURE__ */ (() => typeBoundary(FindingSeverity, "Finding severity is invalid."))();
const category = /* @__PURE__ */ (() => typeBoundary(FindingCategory, "Finding category is invalid."))();
const gateEffect = /* @__PURE__ */ (() => typeBoundary(FindingGateEffect, "Finding gate effect is invalid."))();
const actorScope = /* @__PURE__ */ (() => typeBoundary(FindingActorScope, "Finding actor scope is invalid."))();

const ScopeAction = /* @__PURE__ */ (() => Schema.Struct({
  action: FindingDispositionAction,
  scope: FindingActorScope
}).check(Schema.makeFilter(value => value.scope === "station-role" || value.action === "address")))();
const revocationState = (value: unknown, message: string) =>
  typeBoundary(Schema.UndefinedOr(Schema.Boolean), message)(value);
const immutableField = <Value extends string | number | boolean>(expected: Value, value: unknown): Value =>
  typeBoundary(Schema.Literal(expected))(value);

const TRANSITIONS: Readonly<Record<FindingDispositionAction, Readonly<Partial<Record<FindingLifecycleState, FindingLifecycleState>>>>> = Object.freeze({
  open: Object.freeze({ draft: "open" }),
  address: Object.freeze({ open: "addressed", reopened: "addressed" }),
  resolve: Object.freeze({ addressed: "resolved" }),
  dismiss: Object.freeze({ open: "dismissed", addressed: "dismissed", reopened: "dismissed" }),
  reopen: Object.freeze({ resolved: "reopened", dismissed: "reopened" })
});
function transition(action: FindingDispositionAction, prior: FindingLifecycleState, refuse: Refusal): FindingLifecycleState {
  const states = TRANSITIONS[action];
  const next = Object.hasOwn(states, prior) ? states[prior] : undefined;
  if (next === undefined) refuse("invalid-transition", `Finding cannot ${action} from ${prior}.`);
  return next;
}

function safetyQuorum(finding: FindingDispositionSnapshotInput): boolean {
  return (
    finding.gateEffect === "blocking" &&
    (finding.severity === "critical" || finding.category === "security")
  );
}

function minimumRole(
  action: FindingDispositionAction,
  finding: FindingDispositionSnapshotInput,
  scope: FindingActorScope
): StationRole {
  if (action === "address") {
    return scope === "station-role" ? "reviewer" : "runner";
  }
  if (action === "open" || action === "reopen") return "reviewer";
  return finding.gateEffect === "blocking" ? "maintainer" : "reviewer";
}

function signer(input: FindingAuthorityGrantInput, refuse: Refusal): Readonly<{
  artifact: FindingDispositionSigner;
  role: StationRole;
}> {
  revocationState(input.revoked, "Finding actor revocation state must be boolean.");
  if (input.revoked === true) {
    refuse(
      "stale-authority",
      "A revoked grant cannot disposition a finding."
    );
  }
  return {
    artifact: Object.freeze({
      principalId: developmentIdentifier("principal", input.principalId),
      grantRevision: positiveInteger(
        input.revision,
        "Finding actor grant revision"
      )
    }),
    role: role(input.role, "Finding actor role")
  };
}


export const dispositionIdentity = (rawInput: FindingDispositionInput) => dispositionResult((refuse: Refusal) => {
  const input = snapshot(rawInput);
  const action = dispositionAction(input.action);
  const finding = Object.freeze({
    findingId: developmentIdentifier("finding", input.finding.findingId),
    candidateId: developmentIdentifier("candidate", input.finding.candidateId),
    lifecycle: findingLifecycle(input.finding.lifecycle),
    severity: severity(input.finding.severity),
    category: category(input.finding.category),
    gateEffect: gateEffect(input.finding.gateEffect)
  });
  const scope = actorScope(input.actorScope);
  parse(ScopeAction, () => refuse(
    "insufficient-authority", "Assigned authors and fix runners may only address findings."
  ))({ action, scope });
  if (
    (scope === "assigned-author" || scope === "fix-runner") &&
    input.assignmentVerified !== true
  ) {
    refuse(
      "assignment-required",
      "Runner disposition requires a verified finding assignment."
    );
  }
  if (
    (action === "address" || action === "resolve") &&
    input.evidenceId === undefined
  ) {
    refuse(
      "evidence-required",
      "Addressing or resolving a finding requires exact evidence."
    );
  }
  const first = signer(input.actor, refuse);
  const requiredRole = minimumRole(action, finding, scope);
  if (!atLeastRole(first.role, requiredRole)) {
    refuse(
      "insufficient-authority",
      `Finding ${action} requires at least ${requiredRole}.`
    );
  }
  const safety =
    (action === "resolve" || action === "dismiss") &&
    safetyQuorum(finding);
  let second: ReturnType<typeof signer> | undefined;
  if (safety) {
    if (!input.secondActor) {
      refuse(
        "insufficient-authority",
        "Safety disposition requires two current principals."
      );
    }
    second = signer(input.secondActor, refuse);
    if (
      second.artifact.principalId === first.artifact.principalId ||
      !atLeastRole(second.role, "maintainer") ||
      (first.role !== "owner" && second.role !== "owner")
    ) {
      refuse(
        "insufficient-authority",
        "Safety disposition requires distinct maintainers including one owner."
      );
    }
  } else {
    typeBoundary(Schema.Undefined, "Ordinary disposition cannot include a second actor.")(input.secondActor);
  }
  const reasonCode = token(input.reasonCode, "Finding disposition reason code");
  const reasonDetail = boundedText(input.reasonDetail);
  const withoutDigest = Object.freeze({
    version: FINDING_DISPOSITION_VERSION,
    findingId: finding.findingId,
    candidateId: finding.candidateId,
    action,
    priorState: finding.lifecycle,
    resultingState: transition(action, finding.lifecycle, refuse),
    severity: finding.severity,
    category: finding.category,
    gateEffect: finding.gateEffect,
    actorScope: scope,
    actor: first.artifact,
    ...(second === undefined ? {} : { secondActor: second.artifact }),
    safetyQuorum: safety,
    reasonCode,
    reasonDetail,
    ...(input.evidenceId === undefined
      ? {}
      : { evidenceId: developmentIdentifier("evidence", input.evidenceId) }),
    createdAt: safeTime(input.createdAt)
  });
  return withoutDigest;
});

export const prepareDispositionVerification = (artifact: unknown) => dispositionResult((refuse: Refusal) => {
  // Canonical snapshotting has already rejected caller accessors/prototypes.
  // Every identity field shares the artifact Schema; digest/id and unknown
  // fields remain unchecked until the accepted hash has settled.
  const observed = snapshot(artifact) as FindingDispositionArtifact;
  const identity = parse(FindingDispositionIdentity)(observed);
  const resultingState = transition(identity.action, identity.priorState, refuse);
  Object.freeze(identity.actor);
  if (identity.secondActor !== undefined) Object.freeze(identity.secondActor);
  const withoutDigest = Object.freeze(identity);
  return { observed, withoutDigest, resultingState, observedResultingState: identity.resultingState };
});

type Verification = Result.Result.Success<ReturnType<typeof prepareDispositionVerification>>;
export const completeDispositionVerification = ({ observed, withoutDigest, resultingState, observedResultingState }: Verification, digest: string) => dispositionResult(() => {
  const expected = `sha256:${digest}`, dispositionId = `disposition:v1:${expected}`;
  const normalized = Object.freeze({
    ...withoutDigest,
    dispositionId,
    artifactDigest: expected
  });
  immutableField(expected, observed.artifactDigest);
  immutableField(dispositionId, observed.dispositionId);
  immutableField(resultingState, observedResultingState);
  immutableField(serialize(normalized), serialize(observed));
});

export const prepareDispositionAuthority = (artifact: FindingDispositionArtifact, currentFinding: FindingDispositionSnapshotInput, currentGrants: readonly FindingAuthorityGrantInput[]) => dispositionResult(() => {
  const sealedArtifact = snapshot(artifact) as FindingDispositionArtifact;
  const finding = snapshot(currentFinding);
  const grantInputs = snapshot(currentGrants);
  typeBoundary(IdentityArrayInput, "Current finding grants must be an array.")(grantInputs);
  const normalizedGrants = grantInputs.map((grant) => {
    revocationState(grant.revoked, "Current grant revocation state must be boolean.");
    return Object.freeze({
      principalId: developmentIdentifier("principal", grant.principalId),
      role: role(grant.role, "Current finding grant role"),
      revision: positiveInteger(grant.revision, "Current grant revision"),
      ...(grant.revoked === undefined ? {} : { revoked: grant.revoked })
    });
  });
  return { sealedArtifact, finding, normalizedGrants };
});

type Authority = Result.Result.Success<ReturnType<typeof prepareDispositionAuthority>>;
export const completeDispositionAuthority = ({ sealedArtifact, finding, normalizedGrants }: Authority, assignmentVerified: boolean) => dispositionResult((refuse: Refusal) => {
  if (
    sealedArtifact.findingId !== finding.findingId ||
    sealedArtifact.candidateId !== finding.candidateId ||
    sealedArtifact.priorState !== finding.lifecycle ||
    sealedArtifact.severity !== finding.severity ||
    sealedArtifact.category !== finding.category ||
    sealedArtifact.gateEffect !== finding.gateEffect
  ) {
    refuse(
      "stale-finding",
      "Finding changed after disposition authorization."
    );
  }
  const grants = new Map(
    normalizedGrants.map((grant) => [grant.principalId, grant] as const)
  );
  typeBoundary(UniqueIdentityValues, "Current finding grants must be unique.")(normalizedGrants.map(grant => grant.principalId));
  const assertCurrent = (stored: FindingDispositionSigner): StationRole => {
    const grant = parse(Schema.Struct({
      role: StationRole,
      revision: Schema.Literal(stored.grantRevision),
      revoked: Schema.Literal(false).pipe(Schema.optional)
    }), () => refuse(
      "stale-authority", "Finding disposition grant is no longer current."
    ))(grants.get(stored.principalId));
    return role(grant.role, "Current finding grant role");
  };
  const firstRole = assertCurrent(sealedArtifact.actor);
  const requiredRole = minimumRole(
    sealedArtifact.action,
    finding,
    sealedArtifact.actorScope
  );
  if (!atLeastRole(firstRole, requiredRole)) {
    refuse(
      "stale-authority",
      "Finding actor no longer holds the required role."
    );
  }
  if (
    sealedArtifact.actorScope !== "station-role" &&
    assignmentVerified !== true
  ) {
    refuse(
      "assignment-required",
      "Finding runner assignment is no longer current."
    );
  }
  if (sealedArtifact.safetyQuorum) {
    if (!sealedArtifact.secondActor) {
      refuse(
        "stale-authority",
        "Safety quorum is incomplete."
      );
    }
    const secondRole = assertCurrent(sealedArtifact.secondActor);
    if (
      sealedArtifact.actor.principalId ===
        sealedArtifact.secondActor.principalId ||
      !atLeastRole(firstRole, "maintainer") ||
      !atLeastRole(secondRole, "maintainer") ||
      (firstRole !== "owner" && secondRole !== "owner")
    ) {
      refuse(
        "stale-authority",
        "Safety disposition authority is no longer current."
      );
    }
  }
});

export const encodedFindingDispositionReason = (artifact: Pick<FindingDispositionArtifact, "reasonCode" | "reasonDetail" | "actorScope" | "safetyQuorum">): Result.Result<string, FindingDispositionDependencyError> => Result.try({ try: () => {
  return serialize({
    version: FINDING_DISPOSITION_VERSION,
    code: artifact.reasonCode,
    detail: artifact.reasonDetail,
    actorScope: artifact.actorScope,
    safetyQuorum: artifact.safetyQuorum
  });
}, catch: findingDispositionDependencyError });

export const prepareDispositionRecord = (row: FindingDispositionRow, finding: DurableFindingRecord) => dispositionResult(() => {
  const reason = JSON.parse(row.reason) as Record<string, unknown>;
  const canonicalReason = {
    version: FINDING_DISPOSITION_VERSION,
    code: reason.code, detail: reason.detail,
    actorScope: reason.actorScope, safetyQuorum: reason.safetyQuorum
  };
  parse(Schema.Unknown.check(Schema.makeFilter(value => Boolean(value) && !Array.isArray(value))),
    () => new TypeError("Stored finding disposition reason is invalid."))(reason);
  const encodedReason = serialize(reason);
  parse(Schema.Literal(serialize(canonicalReason)),
    () => new TypeError("Stored finding disposition reason is invalid."))(encodedReason);
  parse(Schema.Tuple([Schema.Unknown, Schema.Unknown]).check(Schema.makeFilter(([principal, revision]) =>
    (principal === null) === (revision === null)
  )), () => new TypeError("Stored second disposition actor is incomplete."))([row.second_principal_id, row.second_grant_revision]);
  const storedSecondActor = row.second_principal_id !== null
    ? Object.freeze({
        principalId: developmentIdentifier("principal", row.second_principal_id),
        grantRevision: row.second_grant_revision
      })
    : undefined;
  const evidenceId = row.evidence_id === null
    ? undefined
    : developmentIdentifier("evidence", row.evidence_id);
  const input = Object.freeze({
    version: FINDING_DISPOSITION_VERSION,
    dispositionId: row.disposition_id,
    artifactDigest: row.disposition_id.slice("disposition:v1:".length),
    findingId: developmentIdentifier("finding", row.finding_id),
    candidateId: developmentIdentifier("candidate", row.candidate_id),
    action: row.action,
    priorState: row.prior_state,
    resultingState: row.resulting_state,
    severity: finding.severity,
    category: finding.category,
    gateEffect: finding.gateEffect,
    actorScope: reason.actorScope,
    actor: Object.freeze({
      principalId: developmentIdentifier("principal", row.actor_principal_id),
      grantRevision: row.actor_grant_revision
    }),
    ...(storedSecondActor === undefined ? {} : { secondActor: storedSecondActor }),
    safetyQuorum: reason.safetyQuorum,
    reasonCode: reason.code,
    reasonDetail: reason.detail,
    ...(evidenceId === undefined ? {} : { evidenceId }),
    createdAt: row.created_at
  });
  return { input, evidenceId };
});

type RecordInput = Result.Result.Success<ReturnType<typeof prepareDispositionRecord>>;
export const completeDispositionRecord = ({ input, evidenceId }: RecordInput): Result.Result<DurableFindingDispositionRecord, FindingDispositionFailure> => dispositionResult(() => {
  const artifact = parse(FindingDispositionArtifact)(input);
  const { actor, secondActor } = artifact;
  return Object.freeze({
    dispositionId: artifact.dispositionId,
    findingId: artifact.findingId,
    action: artifact.action,
    priorState: artifact.priorState,
    resultingState: artifact.resultingState,
    actorPrincipalId: actor.principalId,
    actorGrantRevision: actor.grantRevision,
    ...(secondActor === undefined
      ? {}
      : {
          secondPrincipalId: secondActor.principalId,
          secondGrantRevision: secondActor.grantRevision
        }),
    actorScope: artifact.actorScope,
    safetyQuorum: artifact.safetyQuorum,
    reasonCode: artifact.reasonCode,
    reasonDetail: artifact.reasonDetail,
    ...(evidenceId === undefined ? {} : { evidenceId }),
    candidateId: artifact.candidateId,
    createdAt: artifact.createdAt
  });
});
